import { CustomUserPermissionTabs } from '@react/Pages/UsersPage/UserPermissionSidebar/AppAccess/CustomUserPermissions/utils';
import { TraceStatus } from '@redux/types';

/* eslint-disable @typescript-eslint/naming-convention */
type Permission = {
  role: string;
};

type TrackerPermission = Permission & {
  token: string;
  keywords?: string[];
};

type PartnerPermission = Permission & {
  partner_id: string;
};

export type AppPermission = {
  token: string;
  role?: string;
  partners?: PartnerPermission[];
  trackers?: TrackerPermission[];
};

export enum AppRole {
  OFF = 'off',
  EDITOR = 'editor',
  READER = 'reader',
  CUSTOM = 'custom',
}

export enum TrackerRole {
  custom = 'limited',
  reader = 'on',
  off = 'off',
}

export enum UserPermissionsScreen {
  MAIN = 'main',
  APP_LIST = 'app-list',
  APP_ACCESS = 'app-access',
  TRACKER_ACCESS = 'tracker-access',
  PARTNER_ACCESS = 'partner-access',
}

export type Feature = {
  name: string;
  role: string;
};

export type User = {
  id?: number;
  role?: string;
  email: string;
  super_admin?: boolean;
  local_roles: {
    features: Feature[];
    mobile_apps: AppPermission[];
  };
};

export interface UserPermissionsState {
  user: User;
  isLoading: boolean;
  screen: UserPermissionsScreen;
  currentApp?: any;
  currentTracker?: any;
  currentPartner?: any;
  isSubmitting: boolean;
  isAlertOpen: boolean;
  trace: { status: TraceStatus; message: string };
  trackerKeywords: string[];
  canApplyAppPermissions: boolean;
  canApplyUserPermissions: boolean;
  currentTab: CustomUserPermissionTabs;
}

export const getInitialState = () => ({
  user: {
    email: '',
    role: 'reader',
    local_roles: {
      features: [],
      mobile_apps: [],
    },
  },
  isLoading: false,
  screen: UserPermissionsScreen.MAIN,
  currentApp: undefined,
  currentTracker: undefined,
  currentPartner: undefined,
  isSubmitting: false,
  isAlertOpen: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
  trackerKeywords: [],
  canApplyAppPermissions: false,
  canApplyUserPermissions: false,
  currentTab: CustomUserPermissionTabs.Tracker,
});

export const extractBasePermission = (item): Permission => ({
  role: item.access.role,
});

export const extractPermissionWithToken = item => ({
  ...extractBasePermission(item),
  token: item.token,
});

export const extractTrackerPermission = (tracker): TrackerPermission => {
  const trackerPermission: TrackerPermission = extractPermissionWithToken(tracker);

  if (tracker?.access?.keywords?.length) {
    trackerPermission.keywords = tracker.access.keywords;
  }

  return trackerPermission;
};

export const extractPartnerPermission = (partner): PartnerPermission => ({
  ...extractBasePermission(partner),
  partner_id: String(partner.id),
});

interface extractAppParams {
  app: any;
  trackers?: TrackerPermission[];
  partners?: PartnerPermission[];
}

export const extractAppPermission = ({ app }: extractAppParams) => {
  const appPermissions: AppPermission = extractPermissionWithToken(app);

  if (appPermissions.role === AppRole.CUSTOM && app.access.trackers) {
    appPermissions.trackers = app.access.trackers;
  }

  if (appPermissions.role === AppRole.CUSTOM && app.access.partners) {
    appPermissions.partners = app.access.partners;
  }

  return appPermissions;
};
